import { Directive, ElementRef, HostListener, OnChanges, SimpleChanges, input, model } from '@angular/core';

@Directive({
    selector: '[digitOnly]',
    standalone: true,
})
export class DigitOnlyDirective implements OnChanges {
    private hasDecimalPoint = false;
    private hasNegativeSign = false;
    private readonly navigationKeys = [
        'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
        'Home', 'End', 'ArrowLeft', 'ArrowRight', 'Clear', 'Copy', 'Paste',
    ];

    decimal = input<boolean>(false);
    decimalSeparator = input<string>('.');
    allowNegatives = input<boolean>(false);
    allowPaste = input<boolean>(true);
    negativeSign = input<string>('-');
    min = model<number>(-Infinity);
    max = model<number>(Infinity);
    pattern = input<string | RegExp>();

    private regex: RegExp | null = null;
    inputElement: HTMLInputElement;

    constructor(public el: ElementRef) {
        this.inputElement = el.nativeElement;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['pattern']) {
            this.regex = this.pattern() ? RegExp(this.pattern() as RegExp) : null;
        }
        if (changes['min']) {
            const maybeMin = Number(this.min());
            this.min.set(isNaN(maybeMin) ? -Infinity : maybeMin);
        }
        if (changes['max']) {
            const maybeMax = Number(this.max());
            this.max.set(isNaN(maybeMax) ? Infinity : maybeMax);
        }
    }

    @HostListener('beforeinput', ['$event'])
    onBeforeInput(e: InputEvent): any {
        if (isNaN(Number(e.data))) {
            if (e.data === this.decimalSeparator() || (e.data === this.negativeSign() && this.allowNegatives())) {
                return;
            }
            e.preventDefault();
            e.stopPropagation();
        }
    }

    @HostListener('keydown', ['$event'])
    onKeyDown(e: KeyboardEvent): any {
        if (
            this.navigationKeys.indexOf(e.key) > -1 ||
            ((e.key === 'a' || e.code === 'KeyA') && (e.ctrlKey || e.metaKey)) ||
            ((e.key === 'c' || e.code === 'KeyC') && (e.ctrlKey || e.metaKey)) ||
            ((e.key === 'v' || e.code === 'KeyV') && (e.ctrlKey || e.metaKey)) ||
            ((e.key === 'x' || e.code === 'KeyX') && (e.ctrlKey || e.metaKey))
        ) {
            return;
        }

        let newValue = '';

        if (this.decimal() && e.key === this.decimalSeparator()) {
            newValue = this.forecastValue(e.key);
            if (newValue.split(this.decimalSeparator()).length > 2) {
                e.preventDefault();
                return;
            } else {
                this.hasDecimalPoint = newValue.indexOf(this.decimalSeparator()) > -1;
                return;
            }
        }

        if (e.key === this.negativeSign() && this.allowNegatives()) {
            newValue = this.forecastValue(e.key);
            if (!newValue.startsWith(this.negativeSign()) || newValue.split(this.negativeSign()).length > 2) {
                e.preventDefault();
                return;
            } else {
                this.hasNegativeSign = newValue.split(this.negativeSign()).length > -1;
                return;
            }
        }

        if (e.key === ' ' || isNaN(Number(e.key))) {
            e.preventDefault();
            return;
        }

        newValue = newValue || this.forecastValue(e.key);
        if (this.regex) {
            if (!this.regex.test(newValue)) {
                e.preventDefault();
                return;
            }
        }

        const newNumber = Number(newValue);
        if (newNumber > this.max() || newNumber < this.min()) {
            e.preventDefault();
        }
    }

    @HostListener('paste', ['$event'])
    onPaste(event: any): void {
        if (this.allowPaste()) {
            let pastedInput = '';
            if ((window as any)['clipboardData']) {
                pastedInput = (window as any)['clipboardData'].getData('text');
            } else if (event?.clipboardData?.getData) {
                pastedInput = event.clipboardData.getData('text/plain');
            }
            this.pasteData(pastedInput);
            event.preventDefault();
        } else {
            event.preventDefault();
            event.stopPropagation();
        }
    }

    @HostListener('drop', ['$event'])
    onDrop(event: DragEvent): void {
        const textData = event.dataTransfer?.getData('text') ?? '';
        this.inputElement.focus();
        this.pasteData(textData);
        event.preventDefault();
    }

    private pasteData(pastedContent: string): void {
        const sanitizedContent = this.sanitizeInput(pastedContent);
        if (
            sanitizedContent.includes(this.negativeSign()) &&
            this.hasNegativeSign &&
            !this.getSelection().includes(this.negativeSign())
        ) {
            return;
        }
        const pasted = document.execCommand('insertText', false, sanitizedContent);
        if (!pasted) {
            if (this.inputElement.setRangeText) {
                const { selectionStart: start, selectionEnd: end } = this.inputElement;
                this.inputElement.setRangeText(sanitizedContent, start ?? 0, end ?? 0, 'end');
                if (typeof (window as any)['InstallTrigger'] !== 'undefined') {
                    this.inputElement.dispatchEvent(new Event('input', { cancelable: true }));
                }
            } else {
                this.insertAtCursor(this.inputElement, sanitizedContent);
            }
        }
        if (this.decimal()) {
            this.hasDecimalPoint = this.inputElement.value.indexOf(this.decimalSeparator()) > -1;
        }
        this.hasNegativeSign = this.inputElement.value.indexOf(this.negativeSign()) > -1;
    }

    private insertAtCursor(myField: HTMLInputElement, myValue: string): void {
        const startPos = myField.selectionStart ?? 0;
        const endPos = myField.selectionEnd ?? 0;
        myField.value =
            myField.value.substring(0, startPos) + myValue + myField.value.substring(endPos, myField.value.length);
        const pos = startPos + myValue.length;
        myField.focus();
        myField.setSelectionRange(pos, pos);
        this.triggerEvent(myField, 'input');
    }

    private triggerEvent(el: HTMLInputElement, type: string): void {
        if ('createEvent' in document) {
            const e = document.createEvent('HTMLEvents');
            e.initEvent(type, false, true);
            el.dispatchEvent(e);
        }
    }

    private sanitizeInput(input: string): string {
        let result = '';
        let regex: RegExp;
        if (this.decimal() && this.isValidDecimal(input)) {
            regex = new RegExp(`${this.getNegativeSignRegExp()}[^0-9${this.decimalSeparator()}]`, 'g');
        } else {
            regex = new RegExp(`${this.getNegativeSignRegExp()}[^0-9]`, 'g');
        }
        result = input.replace(regex, '');
        const maxLength = this.inputElement.maxLength;
        if (maxLength > 0) {
            const allowedLength =
                maxLength - this.inputElement.value.length + (result.includes(`${this.negativeSign()}`) ? 1 : 0);
            result = allowedLength > 0 ? result.substring(0, allowedLength) : '';
        }
        return result;
    }

    private getNegativeSignRegExp(): string {
        return this.allowNegatives() && (!this.hasNegativeSign || this.getSelection().includes(this.negativeSign()))
            ? `(?!^${this.negativeSign()})`
            : '';
    }

    private isValidDecimal(string: string): boolean {
        if (!this.hasDecimalPoint) {
            return string.split(this.decimalSeparator()).length <= 2;
        } else {
            const selectedText = this.getSelection();
            if (selectedText && selectedText.indexOf(this.decimalSeparator()) > -1) {
                return string.split(this.decimalSeparator()).length <= 2;
            } else {
                return string.indexOf(this.decimalSeparator()) < 0;
            }
        }
    }

    private getSelection(): string {
        return this.inputElement.value.substring(
            this.inputElement.selectionStart ?? 0,
            this.inputElement.selectionEnd ?? 0,
        );
    }

    private forecastValue(key: string): string {
        const selectionStart = this.inputElement.selectionStart ?? 0;
        const selectionEnd = this.inputElement.selectionEnd ?? 0;
        const oldValue = this.inputElement.value;
        return oldValue.substring(0, selectionStart) + key + oldValue.substring(selectionEnd);
    }
}
